import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { ThemeEmoji } from './ThemeEmoji';

export type ToastKind = 'info' | 'success' | 'error' | 'warning';

interface ToastEntry {
  id: number;
  msg: string;
  kind: ToastKind;
}

type ShowFn = (msg: string, kind?: ToastKind) => void;

const ToastContext = createContext<ShowFn | null>(null);

let __toastId = 0;

const TONE: Record<ToastKind, string> = {
  info: 'bg-sky-600 text-white',
  success: 'bg-emerald-600 text-white',
  error: 'bg-rose-600 text-white',
  warning: 'bg-amber-500 text-white',
};

const AUTO_DISMISS_MS = 3200;

/**
 * ToastHost — provider that renders the queue + dismisses each entry
 * after `AUTO_DISMISS_MS`. Mount once near the root (e.g. inside
 * `<Layout>`); consumers call `useToast()` to surface feedback without
 * threading callbacks down the tree.
 *
 * Source: .planning/v7-design-handoff/v7-sla/shell.jsx lines 202-229
 */
export function ToastHost({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  // F2-010: track per-toast dismiss timeouts so we can clear them on
  // unmount. Without this, a setTimeout fires after the host unmounts and
  // React 18 silently no-ops the setToasts call (StrictMode warnings in
  // dev). Mirror of the cycle-1 F1-009/010 / F25-002 / F22-003 sweep.
  const timeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  useEffect(() => {
    const timeouts = timeoutsRef.current;
    return () => {
      for (const t of timeouts) clearTimeout(t);
      timeouts.clear();
    };
  }, []);
  const show: ShowFn = (msg, kind = 'info') => {
    const id = ++__toastId;
    setToasts((t) => [...t, { id, msg, kind }]);
    const handle = setTimeout(() => {
      setToasts((t) => t.filter((x) => x.id !== id));
      timeoutsRef.current.delete(handle);
    }, AUTO_DISMISS_MS);
    timeoutsRef.current.add(handle);
  };
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className="fixed bottom-6 right-6 flex flex-col gap-2 z-50">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`px-4 py-2 rounded-ms-control shadow-lg text-[12.5px] font-medium ${TONE[t.kind]}`}
          >
            {t.msg}
            {/* MKS-493 — the joy emoji on save/submit CONFIRMATIONS only.
                `success` is the only kind that qualifies: error, warning and
                info are explicitly excluded by the design. */}
            {t.kind === 'success' && <ThemeEmoji role="done" />}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * useToast — returns the `show(msg, kind)` function from the nearest
 * `<ToastHost>`. Throws if called outside a host (developer error,
 * surfaces immediately rather than silently dropping toasts).
 */
export function useToast(): ShowFn {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastHost>');
  return ctx;
}

/** Legacy alias — prefer the explicit `<ToastHost>` name in new code. */
export const Toast = ToastHost;
